export const EFFECT_IDS = [
  'glow',
  'pulse',
  'vignette',
  'light-leak',
  'prism',
  'film-grain',
  'scanlines',
  'dreamy',
] as const;

export type EditorEffectId = (typeof EFFECT_IDS)[number];

/** Shared edge easing used by both the live preview and ffmpeg export. */
export const EFFECT_EDGE_FADE_SECONDS = 0.3;

export function effectEdgeOpacity(
  start: number,
  end: number,
  currentTime: number,
  baseOpacity = 1
): number {
  if (currentTime < start || currentTime > end || end <= start) return 0;
  const fadeDuration = Math.min(EFFECT_EDGE_FADE_SECONDS, (end - start) / 2);
  if (fadeDuration <= 0) return Math.max(0, Math.min(1, baseOpacity));
  const edgeProgress = Math.min(
    1,
    (currentTime - start) / fadeDuration,
    (end - currentTime) / fadeDuration
  );
  return Math.max(0, Math.min(1, baseOpacity * edgeProgress));
}

export interface EditorEffectPreset {
  id: EditorEffectId;
  name: string;
  description: string;
  defaultColor: string;
  defaultOpacity: number;
  blendMode: 'screen' | 'multiply' | 'overlay' | 'soft-light';
}

export const EDITOR_EFFECTS: EditorEffectPreset[] = [
  {
    id: 'glow',
    name: 'Свечение',
    description: 'Мягкий свет в центре',
    defaultColor: '#7c5cff',
    defaultOpacity: 0.52,
    blendMode: 'screen',
  },
  {
    id: 'pulse',
    name: 'Пульс',
    description: 'Световые волны',
    defaultColor: '#8b5cf6',
    defaultOpacity: 0.58,
    blendMode: 'screen',
  },
  {
    id: 'vignette',
    name: 'Виньетка',
    description: 'Фокус на центре кадра',
    defaultColor: '#000000',
    defaultOpacity: 0.7,
    blendMode: 'multiply',
  },
  {
    id: 'light-leak',
    name: 'Засветка',
    description: 'Тёплый свет плёнки',
    defaultColor: '#ff6b35',
    defaultOpacity: 0.55,
    blendMode: 'screen',
  },
  {
    id: 'prism',
    name: 'Призма',
    description: 'Цветное преломление',
    defaultColor: '#22d3ee',
    defaultOpacity: 0.42,
    blendMode: 'screen',
  },
  {
    id: 'film-grain',
    name: 'Плёнка',
    description: 'Живое зерно и фактура',
    defaultColor: '#ffffff',
    defaultOpacity: 0.2,
    blendMode: 'soft-light',
  },
  {
    id: 'scanlines',
    name: 'Сканлайны',
    description: 'Ретро-экран',
    defaultColor: '#111827',
    defaultOpacity: 0.34,
    blendMode: 'multiply',
  },
  {
    id: 'dreamy',
    name: 'Dreamy',
    description: 'Воздушный цветной свет',
    defaultColor: '#c084fc',
    defaultOpacity: 0.48,
    blendMode: 'screen',
  },
];

const EFFECT_BY_ID = new Map<EditorEffectId, EditorEffectPreset>(
  EDITOR_EFFECTS.map((effect) => [effect.id, effect])
);

const LEGACY_EFFECT_IDS: Record<string, EditorEffectId> = {
  glow: 'glow',
  pulse: 'pulse',
  vignette: 'vignette',
};

export function normalizeEffectId(value?: string | null, fallbackLabel?: string | null): EditorEffectId {
  const raw = (value || fallbackLabel || '').trim().toLowerCase();
  if (EFFECT_BY_ID.has(raw as EditorEffectId)) return raw as EditorEffectId;
  return LEGACY_EFFECT_IDS[raw] ?? 'glow';
}

export function getEffectPreset(value?: string | null, fallbackLabel?: string | null): EditorEffectPreset {
  return EFFECT_BY_ID.get(normalizeEffectId(value, fallbackLabel)) ?? EDITOR_EFFECTS[0];
}
